import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi, type CreateJournalEntryPayload, type JournalQuery } from '@/api/finance';

const JOURNAL_KEY  = ['finance', 'journal'] as const;
const ACCOUNTS_KEY = ['finance', 'accounts'] as const;

/** Plan comptable — cache 5 min car ne change quasi jamais en cours de session */
export const useAccounts = () =>
  useQuery({
    queryKey:  ACCOUNTS_KEY,
    queryFn:   () => financeApi.getAccounts(),
    staleTime: 5 * 60_000,
  });

export const useJournal = (query: JournalQuery = {}) =>
  useQuery({
    queryKey:  [...JOURNAL_KEY, query],
    queryFn:   () => financeApi.getJournal(query),
    staleTime: 30_000,
  });

export const useJournalEntry = (id: string | undefined) =>
  useQuery({
    queryKey: [...JOURNAL_KEY, id],
    queryFn:  () => financeApi.getJournalEntry(id!),
    enabled:  !!id,
  });

export const useCreateJournalEntry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateJournalEntryPayload) => financeApi.createJournalEntry(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOURNAL_KEY });
      qc.invalidateQueries({ queryKey: ['finance', 'kpis'] }); // KPIs dépendent des écritures
    },
  });
};

export const usePostJournalEntry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financeApi.postJournalEntry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOURNAL_KEY });
      qc.invalidateQueries({ queryKey: ['finance', 'kpis'] });
    },
  });
};

// ── Grand livre immuable (lot 2, étape 2.3) ─────────────────

const PERIODES_KEY = ['finance', 'periodes'] as const;

export const useContrePasser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => financeApi.contrePasser(id, { motif }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOURNAL_KEY });
      qc.invalidateQueries({ queryKey: ['finance', 'kpis'] });
    },
  });
};

export const usePeriodes = () =>
  useQuery({ queryKey: PERIODES_KEY, queryFn: () => financeApi.getPeriodes(), staleTime: 60_000 });

export const useCloturerPeriode = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financeApi.cloturerPeriode(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: PERIODES_KEY }),
  });
};

export const useRouvrirPeriode = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => financeApi.rouvrirPeriode(id, motif),
    onSuccess: () => qc.invalidateQueries({ queryKey: PERIODES_KEY }),
  });
};

// ── Lettrage (lot 2, étape 2.4) ─────────────────────────────

const LETTRAGE_KEY = ['finance', 'lettrage'] as const;

export const useLignesLettrage = (compte: string, contactId?: string) =>
  useQuery({ queryKey: [...LETTRAGE_KEY, compte, contactId ?? ''], queryFn: () => financeApi.getLignesLettrage(compte, contactId) });

export const useLettrer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineIds: string[]) => financeApi.lettrer(lineIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: LETTRAGE_KEY }),
  });
};

export const useAnnulerLettrage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => financeApi.annulerLettrage(id, motif),
    onSuccess: () => qc.invalidateQueries({ queryKey: LETTRAGE_KEY }),
  });
};

// ── Fournisseurs (lot 2, étape 2.5) ─────────────────────────

const FOURNISSEURS_KEY = ['finance', 'fournisseurs'] as const;

export const useFacturesFournisseurs = (statut?: string) =>
  useQuery({ queryKey: [...FOURNISSEURS_KEY, 'factures', statut ?? ''], queryFn: () => financeApi.getFacturesFournisseurs(statut) });

export const useReceptionsAFacturer = () =>
  useQuery({ queryKey: [...FOURNISSEURS_KEY, 'receptions'], queryFn: () => financeApi.getReceptionsAFacturer() });

export const useCreerFactureFournisseur = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => financeApi.creerFactureFournisseur(payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: FOURNISSEURS_KEY }); qc.invalidateQueries({ queryKey: JOURNAL_KEY }); },
  });
};

export const usePayerFactureFournisseur = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; montantCents: number; mode: string; datePaiement?: string; reference?: string }) =>
      financeApi.payerFactureFournisseur(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FOURNISSEURS_KEY });
      qc.invalidateQueries({ queryKey: JOURNAL_KEY });
      qc.invalidateQueries({ queryKey: ['finance', 'lettrage'] });
    },
  });
};

// ── États comptables (lot 2, étape 2.8) ─────────────────────

export const useEtatBalance = (du?: string, au?: string) =>
  useQuery({ queryKey: ['finance', 'etats', 'balance', du, au], queryFn: () => financeApi.getEtatBalance(du, au) });

export const useEtatGrandLivre = (p: { du?: string; au?: string; compte?: string; classe?: string }, actif = true) =>
  useQuery({ queryKey: ['finance', 'etats', 'grand-livre', p], queryFn: () => financeApi.getEtatGrandLivre(p), enabled: actif });

export const useEtatBalanceAgee = (type: 'clients' | 'fournisseurs', au?: string) =>
  useQuery({ queryKey: ['finance', 'etats', 'balance-agee', type, au], queryFn: () => financeApi.getEtatBalanceAgee(type, au) });
